import React, { useContext } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';

interface Props {
  readonly showModal: boolean;
  /** True to make them a medic, false to remove them as one. Decides every sentence below. */
  readonly makeMedic: boolean;
  /** Who is meant — their name when known, otherwise their email. */
  readonly person: string;
  readonly isLoading?: boolean;
  readonly handleClose: () => void;
  readonly handleAccept: () => void;
}

/**
 * Asks before anyone is made, or stops being, a company medic.
 *
 * The flag is one click in the listing and sits right next to the edit and remove buttons, so
 * without this a stray click quietly changes who colleagues see as their first aider — and who is
 * counted as one in the attendance statistics and the medics export.
 */
const ConfirmMedicChangeModal = ({
  showModal,
  makeMedic,
  person,
  isLoading,
  handleClose,
  handleAccept,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column>
        <span className={style(styles.bodyText)}>
          {makeMedic ? t('confirm-set-medic', { person }) : t('confirm-unset-medic', { person })}
        </span>
        <span className={style(styles.bodyText)}>
          {makeMedic ? t('confirm-set-medic-hint') : t('confirm-unset-medic-hint')}
        </span>

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth loading={isLoading} disabled={isLoading} onClick={handleAccept}>
              {makeMedic ? t('set-medic') : t('unset-medic')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth disabled={isLoading} onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default ConfirmMedicChangeModal;
